/**
 * Owns the coverage-refresh lifecycle: in-flight flag, API call,
 * toast feedback, and parent-supplied success/error callbacks.
 */
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import { postRequestForCoverageRefresh } from "../../../../api/postCall/refreshCoverage";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { logError } from "../../../../utils/customLogger";

interface UseCoverageRefreshResult {
  isRefreshingCoverage: boolean;
  refreshCoverage: () => Promise<void>;
}

/**
 * Returns a stable `refreshCoverage` callback and the `isRefreshingCoverage`
 * flag used to toggle the spinner in the header refresh control.
 */
export const useCoverageRefresh = (
  id: string | undefined,
  onSuccess?: () => void,
  onError?: (error: Error) => void,
): UseCoverageRefreshResult => {
  const [isRefreshingCoverage, setIsRefreshingCoverage] = useState(false);

  const refreshCoverage = useCallback(async () => {
    setIsRefreshingCoverage(true);
    try {
      await postRequestForCoverageRefresh(id ?? "");
      toast.success(ToastMessages.COVERAGE_REFRESH_SUCCESS);
      onSuccess?.();
    } catch (error) {
      logError(error as Error, "Error while refreshing coverage");
      toast.error(ToastMessages.COVERAGE_REFRESH_FAILURE);
      onError?.(error as Error);
    } finally {
      setIsRefreshingCoverage(false);
    }
  }, [id, onSuccess, onError]);

  return { isRefreshingCoverage, refreshCoverage };
};
